import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export default function AnalyticsHeatmap({
  events = [],
  previewPath = "",
}) {
  const columnCount = 18;
  const rowCount = 24;

  const previewWrapRef =
    useRef(null);

  const previewFrameRef =
    useRef(null);

  const [
    previewLayout,
    setPreviewLayout,
  ] = useState({
    width: 1440,
    height: 2200,
    scale: 0.45,
    displayHeight: 650,
  });

  const previewUrl =
    useMemo(() => {
      if (
        !previewPath ||
        typeof window ===
          "undefined"
      ) {
        return "";
      }

      try {
        const url = new URL(
          previewPath,
          window.location.origin
        );

        url.searchParams.set(
          "analyticsPreview",
          "1"
        );

        return `${url.pathname}${url.search}${url.hash}`;
      } catch {
        const joiner =
          previewPath.includes("?")
            ? "&"
            : "?";

        return `${previewPath}${joiner}analyticsPreview=1`;
      }
    }, [previewPath]);

  const measurePreview =
    useCallback(() => {
      const wrapper =
        previewWrapRef.current;

      const frame =
        previewFrameRef.current;

      if (
        !wrapper ||
        !frame
      ) {
        return;
      }

      let pageWidth = 1440;
      let pageHeight = 2200;

      try {
        const frameDocument =
          frame.contentDocument;

        const root =
          frameDocument
            ?.documentElement;

        const body =
          frameDocument?.body;

        pageWidth = Math.max(
          1100,
          root?.scrollWidth || 0,
          body?.scrollWidth || 0
        );

        pageHeight = Math.max(
          1200,
          root?.scrollHeight || 0,
          body?.scrollHeight || 0
        );
      } catch {
        /*
          The normalized heatmap still works
          even if preview sizing fails.
        */
      }

      const availableWidth =
        Math.max(
          320,
          wrapper.clientWidth ||
            900
        );

      const maxDisplayHeight =
        680;

      const scale =
        Math.min(
          availableWidth /
            pageWidth,

          maxDisplayHeight /
            pageHeight
        );

      setPreviewLayout({
        width: pageWidth,
        height: pageHeight,
        scale,

        displayHeight:
          Math.max(
            440,
            Math.min(
              maxDisplayHeight,
              pageHeight *
                scale
            )
          ),
      });
    }, []);

  useEffect(() => {
    if (!previewUrl) {
      return undefined;
    }

    const handleResize =
      () => {
        measurePreview();
      };

    window.addEventListener(
      "resize",
      handleResize
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );
    };
  }, [
    previewUrl,
    measurePreview,
  ]);

  const cells =
    Array.from(
      {
        length:
          columnCount *
          rowCount,
      },
      () => 0
    );

  events.forEach(
    (event) => {
      const x = Number(
        event.documentXPercent ??
          event.xPercent
      );

      const y = Number(
        event.documentYPercent ??
          event.yPercent
      );

      if (
        !Number.isFinite(x) ||
        !Number.isFinite(y)
      ) {
        return;
      }

      const column =
        Math.min(
          columnCount - 1,
          Math.max(
            0,
            Math.floor(
              (x / 100) *
                columnCount
            )
          )
        );

      const row =
        Math.min(
          rowCount - 1,
          Math.max(
            0,
            Math.floor(
              (y / 100) *
                rowCount
            )
          )
        );

      cells[
        row *
          columnCount +
          column
      ] += 1;
    }
  );

  const maxValue =
    Math.max(
      1,
      ...cells
    );

  if (!events.length) {
    return (
      <div className="analytics-heatmap-empty">
        Heatmap data starts
        collecting after the
        click tracker is deployed.
      </div>
    );
  }

  return (
    <div className="analytics-heatmap-shell">
      <div className="analytics-heatmap-browser-bar">
        <span />
        <span />
        <span />

        <strong>
          Normalized full-page
          click density
        </strong>
      </div>

      <div
        ref={previewWrapRef}
        className="analytics-heatmap-preview"
        style={{
          height:
            previewLayout
              .displayHeight,
        }}
      >
        {previewUrl ? (
          <iframe
            ref={
              previewFrameRef
            }
            src={previewUrl}
            title="Analytics heatmap page preview"
            aria-hidden="true"
            tabIndex={-1}
            loading="lazy"

            /*
              The heatmap grid is exported,
              but the underlying iframe is
              omitted from slides/PDFs.
            */
            data-html2canvas-ignore="true"

            onLoad={() => {
              window.setTimeout(
                measurePreview,
                350
              );

              window.setTimeout(
                measurePreview,
                1300
              );
            }}

            style={{
              width:
                previewLayout
                  .width,

              height:
                previewLayout
                  .height,

              transform:
                `scale(${previewLayout.scale})`,
            }}
          />
        ) : (
          <div className="analytics-heatmap-wireframe">
            <div />
            <div />
            <div />
            <div />
          </div>
        )}

        <div
          className="analytics-heatmap-grid"

          style={{
            gridTemplateColumns:
              `repeat(${columnCount}, minmax(0, 1fr))`,

            gridTemplateRows:
              `repeat(${rowCount}, minmax(0, 1fr))`,
          }}
        >
          {cells.map(
            (
              value,
              index
            ) => {
              const intensity =
                value /
                maxValue;

              return (
                <span
                  key={index}

                  title={
                    value
                      ? `${value} click${
                          value ===
                          1
                            ? ""
                            : "s"
                        }`
                      : "No clicks"
                  }

                  style={{
                    background:
                      value
                        ? `rgba(217, 72, 41, ${
                            0.12 +
                            intensity *
                              0.78
                          })`
                        : "transparent",

                    boxShadow:
                      value &&
                      intensity >
                        0.65
                        ? `0 0 ${
                            8 +
                            intensity *
                              18
                          }px rgba(133, 36, 109, ${
                            0.18 +
                            intensity *
                              0.28
                          })`
                        : "none",
                  }}
                />
              );
            }
          )}
        </div>

        <div className="analytics-heatmap-axis-label top">
          Top of page
        </div>

        <div className="analytics-heatmap-axis-label bottom">
          Bottom of page
        </div>
      </div>
    </div>
  );
}